import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getCursosPublicados } from "@/lib/api";
import { COURSES, COMPETENCIA_ORDER } from "@/data/courses";
import { todosLosCursos } from "@/lib/catalogo.mjs";
import { ETAPAS } from "@/lib/etapa.mjs";

/**
 * LO QUE HAY — el catálogo en cifras. Bloque 3 de la portada.
 *
 * LA PRUEBA DE QUE NO ES UNA DEMO, y es lo único que hace. Después de hacer una lección, la
 * pregunta que queda es «¿y hay más?». Hasta hoy la portada no daba una sola cifra de
 * contenido: hablaba de método, de mentores y de arquitectura, y quien quería saber qué se
 * estudia tenía que entrar en `/cursos` a buscarlo.
 *
 * NINGUNA CIFRA ESTÁ ESCRITA AQUÍ. Las dos grandes salen de `/api/cifras`, que son dos
 * `count_documents` sobre lo publicado, y el reparto por competencia sale de contar el
 * catálogo publicado. Es la regla que dejó la portada sin sus «+3500 estudiantes» y su «98 %
 * de satisfacción», que no eran optimistas: eran inventadas, y las leen las familias de
 * menores antes de decidir si se fían. **Si no se puede contar, no se afirma**, y lo vigila
 * `tools/check_cifras.py`.
 *
 * Y SE CUENTA LO PUBLICADO, no lo escrito. Hay más de mil lecciones escritas que aún no están
 * subidas; anunciarlas sería la misma mentira con otra aritmética.
 *
 * SIN CATÁLOGO NO SE PINTA. Igual que la muestra: un bloque que promete cifras y enseña ceros
 * es peor que no estar.
 */
export default function LoQueHay() {
  const [publicados, setPublicados] = useState(null);

  useEffect(() => {
    let vivo = true;
    getCursosPublicados().then((d) => vivo && setPublicados(d || {})).catch(() => {});
    return () => { vivo = false; };
  }, []);

  // CUANTOS CURSOS HAY EN CADA CURSO ESCOLAR, contados del catalogo publicado. Un curso que
  // declara dos etapas —los hay, la sociolinguistica de 1.º y 2.º— cuenta en las dos: no es un
  // total que sumar, es la respuesta a «¿hay algo para mí?» repetida seis veces.
  const porEtapa = useMemo(() => {
    if (!publicados) return [];
    const rutas = COMPETENCIA_ORDER.map((s) => COURSES.find((c) => c.slug === s)).filter(Boolean);
    const cursos = todosLosCursos(publicados, rutas);
    return ETAPAS.map((e) => ({
      ...e,
      cuantos: cursos.filter((c) => (c.etapa || []).includes(e.clave)).length,
    }));
  }, [publicados]);

  if (!porEtapa.some((e) => e.cuantos)) return null;

  return (
    <div className="rounded-ficha bg-white p-6 sm:p-8 h-full"
         data-testid="landing-lo-que-hay">
      <div>
        <div className="font-mono-alpha text-[10px] uppercase tracking-[0.22em] text-alpha-red">
          Para alumnos
        </div>
        {/* LA PREGUNTA QUE CONTESTA ESTE BLOQUE ES UNA, Y NO ES «CUANTO HAY»: eso ya lo dicen
            las dos cifras del hero. Es «¿hay algo de LO MIO?», que es lo que se pregunta un
            chaval de 3.º de la ESO y lo que se pregunta su madre. */}
        <h2 className="font-serif-display text-[30px] sm:text-[36px] leading-[1.08] mt-2">
          ¿Hay algo de lo tuyo? Mira tu curso.
        </h2>
      </div>

      {/* TRES Y TRES. Llegaron a ir los seis en una línea, cuando esta tarjeta ocupaba siete
          columnas; el 3 de noviembre de 2026 pasó a cinco —*«para alumnos debe ser un panel
          más estrecho»*— y en ese ancho seis nombres de curso se aprietan hasta partirse. En
          el móvil bajan a dos. */}
      <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 lg:gap-2.5 mt-6">
        {porEtapa.map((e) => (
          <li key={e.clave}>
            {/* CADA UNO LLEVA AL CATALOGO FILTRADO POR SU CURSO, que es lo que `/cursos` ya
                sabe hacer con `?curso=`. Y el que no tiene nada se pinta apagado y SIN enlace:
                un boton que siempre se puede pulsar y a veces no da nada se lee como que la
                pagina falla. */}
            {e.cuantos ? (
              <Link
                to={`/cursos?curso=${e.clave}`}
                data-testid={`lo-que-hay-${e.clave}`}
                className="block rounded-caja border border-alpha-black/10 px-3 py-3 hover:border-alpha-black/35 transition-colors"
              >
                <span className="block font-serif-display text-[18px] leading-tight whitespace-nowrap">{e.corto}</span>
                <span className="block font-mono-alpha text-[9.5px] uppercase tracking-[0.1em] text-alpha-black/45 mt-1 whitespace-nowrap">
                  {e.cuantos} {e.cuantos === 1 ? "curso" : "cursos"}
                </span>
              </Link>
            ) : (
              <div className="rounded-caja border border-dashed border-alpha-black/10 px-3 py-3 opacity-45">
                <span className="block font-serif-display text-[18px] leading-tight whitespace-nowrap">{e.corto}</span>
                <span className="block font-mono-alpha text-[9.5px] uppercase tracking-[0.1em] text-alpha-black/45 mt-1 whitespace-nowrap">
                  Pronto
                </span>
              </div>
            )}
          </li>
        ))}
      </ul>

    </div>
  );
}
